import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Clock, MapPin, ExternalLink, Trophy } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES, LINKS } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const FixtureRow = ({ f, i, past }) => (
  <Reveal delay={(i % 4) * 0.06}>
    <div className="rounded-2xl border border-white/10 bg-[#122030] p-5 sm:p-6 flex flex-wrap items-center gap-4 hover:border-[#E5AC3C]/40 transition-colors" data-testid={`fixture-card-${past ? "past" : "upcoming"}-${i}`}>
      <div className="shrink-0 w-16 text-center rounded-xl bg-[#0B1520] border border-white/10 py-2">
        <div className="font-head text-xl font-black text-[#E5AC3C]">{(f.date || "").slice(8, 10)}</div>
        <div className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">
          {new Date(`${f.date}T12:00:00`).toLocaleDateString("en-GB", { month: "short" })}
        </div>
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C] border border-[#E5AC3C]/30 rounded-full px-2.5 py-0.5">{f.team}</span>
          {f.competition && <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{f.competition}</span>}
          {f.source === "play-cricket" && (
            <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#2A8C68] border border-[#2A8C68]/40 rounded-full px-2.5 py-0.5" data-testid={`fixture-live-badge-${i}`}>Live · Play-Cricket</span>
          )}
        </div>
        <h3 className="mt-1.5 font-head text-lg font-bold">vs {f.opponent}</h3>
        <p className="mt-1 text-xs text-[#9BB0C1] inline-flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {prettyDate(f.date)}</span>
          {f.time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {f.time}</span>}
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {f.venue === "Away" ? `Away — ${f.opponent}` : "Home — West Park"}</span>
        </p>
        {past && f.result && (
          <p className="mt-2 text-sm font-semibold text-[#2A8C68] inline-flex items-center gap-1.5" data-testid={`fixture-result-${i}`}>
            <Trophy size={13} /> {f.result}
          </p>
        )}
      </div>
    </div>
  </Reveal>
);

export default function Fixtures() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/fixtures`).then((r) => setData(r.data)).catch(() => setData({ upcoming: [], recent: [] }));
  }, []);

  return (
    <main data-testid="fixtures-page">
      <Seo
        title="Fixtures & Results — Whitley Bay Cricket Club"
        description="Upcoming fixtures and recent results for Whitley Bay CC senior and junior teams. Full details on Play-Cricket."
      />
      <PageHeader
        eyebrow="Every ball, every game"
        title="Fixtures &"
        accent="results."
        description="What's coming up and how the teams got on. Full scorecards and live details are on our Play-Cricket site."
        image={IMAGES.batting}
        testId="fixtures-header"
      />

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <h2 className="font-head text-xl sm:text-2xl font-bold tracking-tight mb-6" data-testid="fixtures-upcoming-heading">Upcoming fixtures</h2>
        </Reveal>
        {!data && <p className="text-[#5C7285]" data-testid="fixtures-loading">Loading fixtures…</p>}
        {data && data.upcoming.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-[#122030] p-8 text-center" data-testid="fixtures-empty">
            <p className="text-[#9BB0C1]">New fixtures for the coming season will be published here soon.</p>
            <a href={LINKS.playCricket} target="_blank" rel="noopener noreferrer" data-testid="fixtures-playcricket-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#E5AC3C] hover:text-[#F3C35B] transition-colors">
              View the fixture list on Play-Cricket <ExternalLink size={14} />
            </a>
          </div>
        )}
        <div className="space-y-4">
          {data?.upcoming.map((f, i) => <FixtureRow key={f.id} f={f} i={i} past={false} />)}
        </div>

        {data && data.recent.length > 0 && (
          <>
            <Reveal>
              <h2 className="font-head text-xl sm:text-2xl font-bold tracking-tight mt-14 mb-6" data-testid="fixtures-recent-heading">Recent results</h2>
            </Reveal>
            <div className="space-y-4">
              {data.recent.map((f, i) => <FixtureRow key={f.id} f={f} i={i} past={true} />)}
            </div>
          </>
        )}

        <Reveal>
          <p className="mt-10 text-xs text-[#5C7285] text-center">
            League fixtures and results sync automatically from Play-Cricket; the club can add extra fixtures manually. Scorecards, league tables and live match centres:{" "}
            <a href={LINKS.playCricket} target="_blank" rel="noopener noreferrer" className="text-[#E5AC3C] hover:text-[#F3C35B]" data-testid="fixtures-footer-playcricket-link">
              whitleybay.play-cricket.com
            </a>
          </p>
        </Reveal>
      </section>
    </main>
  );
}
